import React, { useState } from 'react';
import { Form, Input, Button } from 'antd';
import { useHistory } from 'react-router-dom';
import { apiClient, useAuth } from '@isocel/front-common';

import { AppUser } from '../../queries/api/types';
import { useNotification } from '../notification/Notification';

import './changePassword.less';

interface ChangePasswordFormValues {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const ChangePassword: React.FC = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showNotification } = useNotification();
    const history = useHistory();
    const { user, refetchUser } = useAuth();
    const appUser = user as AppUser;
    const isMountedRef = React.useRef(true);

    React.useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    // Redirect if user doesn't need to change password
    React.useEffect(() => {
        if (appUser && appUser.needPasswordChange === false) {
            if (appUser?.role?.name === 'StoreUser') {
                history.push('/receptionStore');
            } else {
                history.push('/dashboard');
            }
        }
    }, [appUser, history]);

    const handleFinish = async (values: ChangePasswordFormValues) => {
        if (values.newPassword !== values.confirmPassword) {
            showNotification('error', 'Erreur', 'Les mots de passe ne correspondent pas.');
            return;
        }
        setIsSubmitting(true);
        try {
            await apiClient().post('/auth/change-password', {
                currentPassword: values.currentPassword,
                newPassword: values.newPassword,
            });
            showNotification('success', 'Succès', 'Mot de passe changé avec succès.');
            // Refresh the user so needPasswordChange is false before navigating
            await refetchUser();
        } catch (error) {
            showNotification('error', 'Erreur', 'Impossible de changer le mot de passe.');
        } finally {
            if (isMountedRef.current) {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <div className="change-password-page">
            <h1>Changer le mot de passe</h1>
            <Form layout="vertical" onFinish={handleFinish}>
                <Form.Item
                    label="Ancien mot de passe"
                    name="currentPassword"
                    rules={[{ required: true, message: 'Veuillez entrer votre ancien mot de passe.' }]}
                >
                    <Input.Password />
                </Form.Item>
                <Form.Item
                    label="Nouveau mot de passe"
                    name="newPassword"
                    rules={[{ required: true, message: 'Veuillez entrer un nouveau mot de passe.' }]}
                    tooltip="Le mot de passe doit contenir au moins 12 caractères, une majuscule, un chiffre et un caractère spécial."
                >
                    <Input.Password />
                </Form.Item>
                <Form.Item
                    label="Confirmer le nouveau mot de passe"
                    name="confirmPassword"
                    rules={[{ required: true, message: 'Veuillez confirmer le nouveau mot de passe.' }]}
                >
                    <Input.Password />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={isSubmitting}>
                    Changer le mot de passe
                </Button>
            </Form>
        </div>
    );
};

export default ChangePassword;
